import type { CartSummaryVM } from './CartReview';
import './BookingComponents.css';

interface CartBottomBarProps {
  cart: CartSummaryVM & { totalDurationMinutes?: number };
  language?: string;
  /** Etiqueta del botón principal (dinámico según contexto). */
  primaryLabel: string;
  onPrimary: () => void;
  /** Oculta el botón (p.ej. cuando ya estamos en la pantalla del carrito). */
  hidePrimary?: boolean;
}

const L: Record<string, Record<string, string>> = {
  es: { services: 'servicio', servicesPlural: 'servicios', free: 'Gratuito', min: 'min', h: 'h', empty: 'Tu reserva', emptyHint: 'Añade tu primer servicio' },
  en: { services: 'service', servicesPlural: 'services', free: 'Free', min: 'min', h: 'h', empty: 'Your booking', emptyHint: 'Add your first service' },
};

function formatMoney(cents: number, currency?: string | null): string {
  const amount = (cents / 100).toFixed(2);
  return currency ? `${amount} ${currency.toUpperCase()}` : amount;
}

function formatDuration(mins: number, t: Record<string, string>): string {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}${t.h} ${m}${t.min}`;
  if (h) return `${h}${t.h}`;
  return `${m} ${t.min}`;
}

/**
 * Barra inferior fija visible durante todo el flujo cuando la reserva tiene al menos un servicio.
 * Refleja el estado del carrito (nº servicios, duración, total) y ofrece la acción principal
 * contextual. El componente es siempre el mismo; solo cambia su contenido/acción.
 */
export default function CartBottomBar({
  cart,
  language = 'es',
  primaryLabel,
  onPrimary,
  hidePrimary = false,
}: CartBottomBarProps) {
  const t = L[language] ?? L.es;
  const count = cart?.count ?? cart?.items?.length ?? 0;
  const isFree = (cart?.paymentMode ?? ((cart?.totalAmount ?? 0) > 0 ? 'prepaid' : 'free')) === 'free';
  const duration = formatDuration(cart?.totalDurationMinutes ?? 0, t);
  const isEmpty = count < 1;

  return (
    <div className="cart-bottom-bar" role="region" aria-label="cart">
      <div className="cart-bottom-info">
        {isEmpty ? (
          <>
            <div className="cart-bottom-total">{t.empty}</div>
            <div className="cart-bottom-meta">{t.emptyHint}</div>
          </>
        ) : (
          <>
            <div className="cart-bottom-total">
              {isFree ? t.free : formatMoney(cart.totalAmount, cart.currency)}
            </div>
            <div className="cart-bottom-meta">
              {count} {count === 1 ? t.services : t.servicesPlural}
              {duration ? ` · ${duration}` : ''}
            </div>
          </>
        )}
      </div>
      {!hidePrimary && !isEmpty ? (
        <button type="button" className="btn-primary cart-bottom-cta" onClick={onPrimary}>
          {primaryLabel}
        </button>
      ) : null}
    </div>
  );
}
